import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqData = [
    {
      question: "How much does process serving cost in California?",
      answer: "Pricing depends on the service level, location, and urgency. Email your case specifics to quote@legaldocspecialist.com for a quote - include the documents to be served, the service address, and your deadline."
    },
    {
      question: "How quickly can documents be served?",
      answer: "We offer four service levels for service attempts: Routine (4 days), Priority (Next Business Day), Urgent (Same Day), and On-Demand (under 2 hours). Please note: These timelines refer to our attempts to serve documents. We cannot guarantee successful service as this depends on the availability and cooperation of the person being served."
    },
    {
      question: "Where can I find a process server near me in California?",
      answer: "Legal Document Specialist serves all 58 California counties with local process servers. We're based in Palm Springs and serve the entire Coachella Valley, Hi-Desert, and Central Riverside County areas with same-day service available."
    },
    {
      question: "What is the best process serving company in California?",
      answer: "Legal Document Specialist has over 22 years of experience serving California with registered and bonded process servers. We specialize in same-day emergency service and have served law firms and individuals throughout all 58 California counties."
    },
    {
      question: "Do you serve documents outside of California?",
      answer: "Yes! We're based in Palm Springs, California, but we help our California clients execute legal support services nationwide across all 50 states. Our registered process servers operate in jurisdictions throughout the country."
    },
    {
      question: "What types of legal documents can you serve?",
      answer: "We serve all types of legal documents including summons and complaints, subpoenas, divorce papers, restraining orders, eviction notices, small claims documents, court orders, family law documents, and more. Our process servers are experienced with all court requirements."
    },
    {
      question: "How do I get proof that documents were served?",
      answer: "We provide detailed proof of service documents with GPS data, timestamps, and complete service details as required by law. You'll receive regular updates via email and phone throughout the process."
    },
    {
      question: "What are your rates?",
      answer: "Our rates vary based on the type of service, location, and urgency. Email your case specifics to quote@legaldocspecialist.com for a quote tailored to your specific needs."
    },
    {
      question: "Do you serve legal documents in all California counties?",
      answer: "Yes, we provide process serving and legal document services across all 58 California counties. We have a network of registered and bonded process servers throughout the state, ensuring reliable service everywhere in California."
    },
    {
      question: "What is court filing and do you offer e-filing services?",
      answer: "Court filing is the process of submitting legal documents to the court. We offer both traditional and electronic filing (e-filing) services for California courts. E-filing is available 24/7 and provides faster processing and confirmation."
    },
    {
      question: "Can you retrieve documents from courts and government agencies?",
      answer: "Yes, we provide comprehensive document retrieval services from courts, county recorders, and other government agencies throughout California. We can obtain case files, recorded documents, vital records, and more."
    },
    {
      question: "How do I track the status of my process serving order?",
      answer: "After placing your order, you'll receive regular updates via email. We provide detailed affidavits of service with GPS data when service is completed. You can also email support@legaldocspecialist.com anytime for status updates."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // FAQ Schema for search engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  React.useEffect(() => {
    // Add FAQ Schema to page
    const existingFAQSchema = document.querySelector('#faq-schema-data');
    if (existingFAQSchema) {
      existingFAQSchema.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-data';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const schemaElement = document.querySelector('#faq-schema-data');
      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, []);

  return (
    <section id="faq" style={{ 
      backgroundColor: 'var(--bg-card)', 
      padding: '120px 0' 
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Get answers to common questions about our legal document services, process serving, and court filing throughout California.
          </p>
        </div>

        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {faqData.map((faq, index) => (
            <div 
              key={index}
              style={{
                backgroundColor: 'var(--bg-page)',
                borderRadius: '12px',
                marginBottom: '1rem',
                border: '1px solid var(--border-medium)',
                overflow: 'hidden'
              }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                style={{
                  width: '100%',
                  padding: '1.5rem 2rem',
                  textAlign: 'left',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: 'var(--text-primary)'
                }}
                className="faq-question"
              >
                <span>{faq.question}</span>
                {openFAQ === index ? (
                  <ChevronUp size={24} color="var(--brand-primary)" />
                ) : (
                  <ChevronDown size={24} color="var(--brand-primary)" />
                )}
              </button>
              
              {openFAQ === index && (
                <div style={{
                  padding: '0 2rem 2rem 2rem',
                  borderTop: '1px solid var(--border-light)',
                  backgroundColor: 'var(--bg-page)'
                }}>
                  <p className="body-small" style={{
                    color: 'var(--text-secondary)',
                    lineHeight: '1.6',
                    marginTop: '1rem'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA after FAQ */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '3rem 2rem',
          backgroundColor: 'var(--bg-page)',
          borderRadius: '12px',
          border: '1px solid var(--border-medium)'
        }}>
          <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
            Still Have Questions?
          </h3>
          <p className="body-medium" style={{ 
            color: 'var(--text-secondary)',
            marginBottom: '2rem'
          }}>
            Our experienced team is here to help with all your legal document needs.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/quote"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Request a Quote
            </a>
            <a 
              href="mailto:support@legaldocspecialist.com"
              className="btn-secondary"
              style={{ textDecoration: 'none' }}
            >
              Email Us
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .faq-question:hover {
          background-color: var(--bg-hover) !important;
        }
      `}</style>
    </section>
  );
};

export default FAQ;